/**
 * Strategic Partnership Framework between HOLOL and sports clubs.
 * Sourced from public/sportsclubs/text.txt; block shapes are documented in pages/MediaInitiativePage.js.
 */
export const sportsClubsPageEn = {
  hero: {
    title: 'Strategic Partnership Framework',
    subtitle: 'Between HOLOL Global Real Estate Club and Sports Clubs',
    tagline: 'The convergence of sporting elite and real estate',
    lead: 'An Integrated Model for Sports, Economic and International Media Cooperation',
    founder: 'Dr. Adham Elsharkawy — Founder & Chairman',
  },
  stats: [
    { value: '20+', label: 'Services across key areas' },
    { value: '15', label: 'Markets in the initial phase' },
    { value: '100', label: 'Countries within five years' },
    { value: '5', label: 'Steps to activate the partnership' },
  ],
  intro: {
    eyebrow: 'Introduction',
    title: 'A partnership that extends the value of sport',
    paragraphs: [
      'HOLOL Global Real Estate Club proposes a strategic partnership model for leading sports clubs, designed to complement their sporting activities with a wider range of services, economic opportunities and media initiatives, while connecting the club and its members to an international network of professional services, business relationships and cross-border cooperation.',
      'The objective is to establish a mutually beneficial partnership that combines the capabilities and identity of each sports club with HOLOL’s integrated service network, international reach and media capabilities.',
    ],
    imageAlt: 'HOLOL Global Real Estate Club — the convergence of sporting elite and real estate',
  },
  blocks: [
    {
      icon: 'apartment',
      title: 'About HOLOL Global Real Estate Club',
      wide: true,
      paragraphs: [
        'HOLOL Global Real Estate Club is an international ecosystem connecting individuals, businesses, investors and specialist service providers through an integrated network of real estate and professional services, smart memberships, economic media, commercial relationships and international opportunities.',
        'Unlike traditional real estate brokerage models or property-listing platforms, HOLOL operates through a membership-based model designed to provide access to a broad range of services and specialist providers. Its model replaces traditional real estate brokerage commissions with smart memberships, offering members access to the Club’s service network and membership benefits under the applicable terms.',
        'HOLOL offers more than 20 services across several key areas:',
      ],
      labeled: [
        {
          label: 'Real Estate Services',
          text: 'Property sales, purchases, rentals, exchanges, valuation, financing and development.',
        },
        {
          label: 'Property Management and Related Services',
          text: 'Property management, maintenance, finishing, interior design, decoration, furnishing and building materials.',
        },
        {
          label: 'Professional and Legal Services',
          text: 'Legal consultancy, accounting, taxation and specialist services related to real estate and commercial transactions.',
        },
        {
          label: 'Residency and International Mobility Services',
          text: 'Residency, citizenship and immigration-related services, subject to the laws and procedures applicable in each country.',
        },
        {
          label: 'Additional Services and Opportunities',
          text: 'Real estate auctions, development opportunities, strategic partnerships and other services relevant to members and business communities.',
        },
      ],
      closing:
        'These services are supported by an international network targeting 15 markets in its initial phase, with a strategic ambition to expand to 100 countries within five years. HOLOL’s media capabilities include television programmes, digital platforms, press, advertising campaigns, events and exhibitions.',
    },
    {
      icon: 'handshake',
      title: 'The Strategic Partnership Concept',
      wide: true,
      highlight: true,
      paragraphs: [
        'The proposed partnership is founded on the principle of complementarity: each party retains its identity, independence and core activities, while working together to develop services, benefits and joint initiatives that create additional value for both parties and connect the sports club to HOLOL’s international ecosystem.',
      ],
    },
    {
      icon: 'stadium',
      title: 'Value for the Sports Club',
      paragraphs: [
        'The partnership would give the sports club the opportunity to benefit from HOLOL’s services and international network in areas relevant to its needs and development plans, including:',
      ],
      numbered: [
        {
          label: 'Services and Asset Management',
          text: 'Access to specialist companies and service providers in real estate, property management, maintenance, facilities fit-out, valuation, financing and other relevant areas to support the club’s operational and property-related needs.',
        },
        {
          label: 'Development and Partnerships',
          text: 'Opportunities to explore cooperation with companies, investors and institutions across different markets in support of the club’s development plans, property assets and projects.',
        },
        {
          label: 'Media Value',
          text: 'Opportunities for visibility through television programmes, media content, advertising campaigns and joint events, in line with plans agreed by both parties.',
        },
        {
          label: 'International Reach',
          text: 'Access to relevant companies, institutions and potential partners through HOLOL’s network and international relationships, where suitable opportunities for cooperation arise.',
        },
      ],
    },
    {
      icon: 'card_membership',
      title: 'Added Value for Sports Club Members',
      paragraphs: [
        'The partnership may provide an additional membership or a dedicated benefits package for eligible members of participating sports clubs, allowing them to access HOLOL’s service network alongside their existing sports membership.',
        'Potential benefits may include access to:',
      ],
      chips: [
        'Real estate services',
        'Professional services',
        'Property management',
        'Valuation & financing',
        'Maintenance & fit-out',
        'Legal & accounting',
        'Residency & immigration',
      ],
      closing: [
        'Special offers, preferential terms or additional benefits may also be developed specifically for participating club members, subject to mutual agreement and service availability.',
        'HOLOL membership or benefits would supplement the existing sports membership, not replace it or alter the member’s existing sporting rights and privileges. Eligibility, access conditions and the implementation process would be agreed upon in advance.',
      ],
    },
    {
      icon: 'live_tv',
      title: 'Media and Television Partnership',
      paragraphs: [
        'Media is a key component of HOLOL’s model and offers a potential area of cooperation through television programmes, advertising campaigns, digital and press content, and relevant events.',
        'Cooperation may include:',
      ],
      chips: [
        'Jointly produced content',
        'Interviews & programme appearances',
        'Promotional campaigns',
        'Exhibitions & business forums',
        'International visibility',
      ],
      closing:
        'The scope of media participation, distribution channels, schedule, brand and logo usage rights, and any associated costs would be defined through a clear agreement between the parties, ensuring alignment with the club’s identity and objectives.',
    },
    {
      icon: 'public',
      title: 'International Cooperation and Market Expansion',
      paragraphs: [
        'HOLOL’s international network provides a framework for exploring cross-border cooperation between sports clubs and relevant companies, investors, institutions and potential partners.',
        'Depending on the club’s objectives and the nature of each opportunity, this may include joint projects, development and investment opportunities, international media initiatives, events, and the exchange of professional expertise and business relationships.',
      ],
      closing:
        'Any proposed project or transaction would remain subject to the relevant parties’ approval, assessment and agreed procedures.',
    },
    {
      icon: 'route',
      title: 'Partnership Activation Framework',
      wide: true,
      paragraphs: ['The partnership would be developed through a practical, mutually agreed process:'],
      numbered: [
        {
          label: 'Identify Needs and Priorities',
          text: 'Determine the club’s service requirements and priority areas for cooperation.',
        },
        {
          label: 'Define the Scope of Cooperation',
          text: 'Select the services, member benefits, media initiatives and international opportunities most relevant to the club and its members.',
        },
        {
          label: 'Agree on the Partnership Model',
          text: 'Establish the arrangements for the club’s participation, any additional membership benefits, responsibilities and applicable financial or operational terms.',
        },
        {
          label: 'Develop an Initial Programme',
          text: 'Agree on a defined set of services, activities or joint initiatives to be implemented.',
        },
        {
          label: 'Review and Develop',
          text: 'Evaluate the results and identify opportunities to expand or refine the partnership.',
        },
      ],
      closing:
        'This flexible approach allows the cooperation model to be tailored to each club, recognising that the needs and priorities of sports organisations may differ.',
    },
    {
      icon: 'flag',
      title: 'Conclusion',
      wide: true,
      highlight: true,
      paragraphs: [
        'This proposal offers an opportunity to develop a broader model of cooperation between sports clubs and international economic and media ecosystems. It extends the potential value of a sports partnership beyond sporting activities to include additional services for members, development opportunities, media collaboration and access to international business networks.',
        'HOLOL Global Real Estate Club seeks to establish strategic partnerships with sports clubs based on complementarity and mutual benefit, translating the partnership into practical services and initiatives aligned with each club’s objectives.',
      ],
    },
  ],
  closing: {
    eyebrow: 'HOLOL Global Real Estate Club',
    lines: ['Complementarity.', 'Mutual benefit.', 'Practical partnership.'],
    tagline: 'An International Network for Services, Business Relationships, Opportunities and Economic Media',
    name: 'Dr. Adham Elsharkawy',
    role: 'Founder & Chairman',
    websiteLabel: 'Website',
  },
};

export const sportsClubsPageAr = {
  hero: {
    title: 'إطار الشراكة الاستراتيجية',
    subtitle: 'بين نادي حلول العقاري العالمي والأندية الرياضية',
    tagline: 'التقاء النخبة الرياضية بالعقارات',
    lead: 'نحو نموذج متكامل للتعاون الرياضي والاقتصادي والإعلامي الدولي',
    founder: 'د. أدهم الشرقاوي — المؤسس ورئيس مجلس الإدارة',
  },
  stats: [
    { value: '+20', label: 'خدمة متنوعة' },
    { value: '15', label: 'سوقًا في المرحلة الأولى' },
    { value: '100', label: 'دولة خلال خمس سنوات' },
    { value: '5', label: 'خطوات لتفعيل الشراكة' },
  ],
  intro: {
    eyebrow: 'مقدمة',
    title: 'شراكة تمتد بقيمة النشاط الرياضي',
    paragraphs: [
      'يقترح نادي حلول العقاري العالمي نموذجًا للشراكة مع الأندية الرياضية الكبرى، يضيف إلى نشاطها الرياضي منظومة من الخدمات والمزايا الاقتصادية والإعلامية، ويفتح أمام النادي وأعضائه مجالات أوسع للاستفادة من شبكة دولية للخدمات والعلاقات وفرص التعاون.',
      'وتهدف هذه الشراكة إلى تحقيق قيمة عملية للطرفين، من خلال التكامل بين المؤسسة الرياضية وإمكاناتها، ومنظومة نادي حلول العقاري العالمي وخدماتها وانتشارها الإعلامي والدولي.',
    ],
    imageAlt: 'نادي حلول العقاري العالمي — التقاء النخبة الرياضية بالعقارات',
  },
  blocks: [
    {
      icon: 'apartment',
      title: 'ما هو نادي حلول العقاري العالمي؟',
      wide: true,
      paragraphs: [
        'نادي حلول العقاري العالمي منظومة دولية تجمع بين الخدمات العقارية والمهنية، والعضويات الذكية، والإعلام الاقتصادي، والعلاقات التجارية، بهدف إتاحة مجموعة متكاملة من الخدمات وربط الأعضاء بمقدميها في الأسواق المختلفة.',
        'ويعتمد النادي على نموذج العضويات الذكية بدلًا من العمولات العقارية التقليدية، بما يتيح للأعضاء الوصول إلى شبكة الخدمات والاستفادة من المزايا المرتبطة بعضويتهم، وفقًا لنظام النادي وشروط كل خدمة.',
        'ويقدم النادي أكثر من 20 خدمة متنوعة، تشمل:',
      ],
      labeled: [
        {
          label: 'الخدمات العقارية',
          text: 'البيع والشراء والتأجير والتبادل والتقييم والتمويل والتطوير العقاري.',
        },
        {
          label: 'خدمات العقارات وإدارتها',
          text: 'إدارة الممتلكات، والصيانة، والتشطيب، والتصميم الداخلي، والديكور، والتأثيث، ومواد البناء.',
        },
        {
          label: 'الخدمات المهنية والقانونية',
          text: 'الاستشارات القانونية، والمحاسبة، والضرائب، والخدمات المتخصصة المرتبطة بالمعاملات العقارية والتجارية.',
        },
        {
          label: 'خدمات الإقامة والتنقل الدولي',
          text: 'خدمات الإقامة والجنسية والهجرة، وفق القوانين والإجراءات المعمول بها في كل دولة.',
        },
        {
          label: 'خدمات وفرص أخرى',
          text: 'المزادات العقارية، وفرص التطوير والشراكات، والخدمات ذات الصلة باحتياجات الأعضاء ومجتمعات الأعمال.',
        },
      ],
      closing:
        'وتتكامل هذه الخدمات مع شبكة دولية تستهدف 15 سوقًا في المرحلة الأولى، مع خطة للتوسع إلى 100 دولة خلال خمس سنوات، إلى جانب ذراع إعلامية تشمل البرامج التلفزيونية والمنصات الرقمية والصحافة والإعلانات والفعاليات والمعارض.',
    },
    {
      icon: 'handshake',
      title: 'مفهوم الشراكة الاستراتيجية',
      wide: true,
      highlight: true,
      paragraphs: [
        'تقوم الشراكة المقترحة على مبدأ التكامل؛ بحيث يحتفظ كل طرف بهويته واستقلاله ونشاطه الأساسي، بينما يتعاون الطرفان في تطوير خدمات ومزايا ومبادرات مشتركة تحقق قيمة مضافة لكل منهما، وتتيح للنادي الرياضي الاستفادة من منظومة نادي حلول العقاري العالمي على المستوى المحلي والدولي.',
      ],
    },
    {
      icon: 'stadium',
      title: 'القيمة المضافة للنادي الرياضي',
      paragraphs: [
        'تمنح الشراكة النادي الرياضي فرصة للاستفادة من خدمات نادي حلول وشبكته الدولية في المجالات التي تتناسب مع احتياجاته وخططه، ومن أبرزها:',
      ],
      numbered: [
        {
          label: 'الخدمات وإدارة الأصول',
          text: 'الوصول إلى شركات ومقدمي خدمات متخصصين في العقارات وإدارة الممتلكات والصيانة والتجهيز والتقييم والتمويل وغيرها، بما يدعم احتياجات النادي ومشروعاته.',
        },
        {
          label: 'التطوير والشراكات',
          text: 'بحث فرص التعاون مع شركات ومستثمرين ومؤسسات في الأسواق المختلفة، بما يخدم خطط تطوير النادي وممتلكاته ومبادراته.',
        },
        {
          label: 'القيمة الإعلامية',
          text: 'الاستفادة من فرص الظهور في البرامج التلفزيونية والمحتوى الإعلامي والحملات الإعلانية والفعاليات المشتركة، وفق خطط يتم الاتفاق عليها بما يتناسب مع أهداف النادي.',
        },
        {
          label: 'توسيع نطاق العلاقات',
          text: 'الاستفادة من شبكة نادي حلول وشركائه للوصول إلى جهات وشركات ومؤسسات في الأسواق المستهدفة، متى توافرت فرص تعاون مناسبة.',
        },
      ],
    },
    {
      icon: 'card_membership',
      title: 'القيمة المضافة لأعضاء النادي الرياضي',
      paragraphs: [
        'تتيح الشراكة إمكانية توفير عضوية إضافية أو باقة مزايا خاصة لأعضاء النادي الرياضي، تتيح لهم الاستفادة من شبكة خدمات نادي حلول العقاري العالمي، إلى جانب عضويتهم الرياضية الأصلية.',
        'وتشمل مجالات الاستفادة المحتملة:',
      ],
      chips: [
        'الخدمات العقارية والمهنية',
        'إدارة الممتلكات',
        'التقييم والتمويل',
        'الصيانة والتجهيز',
        'الخدمات القانونية والمحاسبية',
        'الإقامة والجنسية والهجرة',
      ],
      closing: [
        'كما يمكن الاتفاق على مزايا أو عروض خاصة لأعضاء النادي الرياضي، بما يضيف قيمة عملية إلى عضويتهم ويوفر لهم إمكانية الوصول إلى شبكة متنوعة من مقدمي الخدمات في الأسواق التي تتوافر فيها هذه الخدمات.',
        'وتظل العضوية الرياضية قائمة بكامل طبيعتها وحقوقها؛ فالعضوية أو المزايا المقدمة من نادي حلول العقاري العالمي تمثل إضافة مستقلة إليها، ولا تحل محلها أو تنتقص منها. ويتم الاتفاق مسبقًا على شروط الاستفادة وآلية تفعيل هذه المزايا.',
      ],
    },
    {
      icon: 'live_tv',
      title: 'الشراكة الإعلامية والتلفزيونية',
      paragraphs: [
        'يمثل الإعلام أحد أهم محاور التعاون المقترح، من خلال الاستفادة من البرامج التلفزيونية والإعلامية والحملات الإعلانية والمحتوى الرقمي والصحفي والفعاليات، لإبراز النادي الرياضي وتاريخه وإنجازاته ومبادراته ومشروعاته.',
        'ويمكن أن يشمل التعاون:',
      ],
      chips: [
        'إنتاج محتوى مشترك',
        'استضافة ممثلي النادي في البرامج',
        'حملات إعلامية وترويجية',
        'فعاليات ومعارض ومنتديات',
        'الظهور في الأسواق الدولية',
      ],
      closing:
        'ويتم تحديد نطاق المشاركة الإعلامية وقنواتها وجدولها الزمني وحقوق استخدام الاسم والشعار وأي تكاليف مرتبطة بها وفق اتفاق واضح بين الطرفين، بما يضمن توافق النشاط الإعلامي مع هوية النادي وأهدافه.',
    },
    {
      icon: 'public',
      title: 'التعاون الدولي وفرص التوسع',
      paragraphs: [
        'تتيح شبكة نادي حلول العقاري العالمي إمكانية بحث فرص تعاون عابرة للحدود تربط النادي الرياضي بالشركات والمستثمرين والمؤسسات والشركاء في الأسواق المختلفة.',
        'وقد يشمل ذلك مشروعات مشتركة، أو فرص تطوير واستثمار، أو مبادرات إعلامية وفعاليات دولية، أو تبادل الخبرات والعلاقات المهنية، بحسب احتياجات النادي وطبيعة كل فرصة.',
      ],
      closing: 'وتخضع أي مشروعات أو تعاملات لموافقة الأطراف المعنية وإجراءات التقييم والاتفاق الخاصة بها.',
    },
    {
      icon: 'route',
      title: 'آلية تفعيل الشراكة',
      wide: true,
      paragraphs: ['يتم تفعيل التعاون من خلال خطوات عملية متفق عليها بين الطرفين:'],
      numbered: [
        'تحديد احتياجات النادي ومجالات التعاون ذات الأولوية.',
        'تحديد الخدمات والمزايا التي يمكن توفيرها للنادي وأعضائه، وآلية الاستفادة منها.',
        'الاتفاق على مجالات التعاون الإعلامي والمبادرات المشتركة المناسبة.',
        'وضع برنامج تنفيذي يحدد المسؤوليات والحقوق والشروط المالية والتشغيلية، إن وجدت.',
        'مراجعة النتائج وتطوير مجالات التعاون وفق ما يحققه البرنامج من قيمة للطرفين.',
      ],
      closing:
        'ويتيح هذا الأسلوب تصميم شراكة تناسب طبيعة كل نادٍ، دون افتراض أن جميع الأندية لديها الاحتياجات نفسها أو أن نموذجًا واحدًا يصلح للجميع.',
    },
    {
      icon: 'flag',
      title: 'خاتمة',
      wide: true,
      highlight: true,
      paragraphs: [
        'يمثل هذا المقترح فرصة لتطوير نموذج جديد للتعاون بين الأندية الرياضية والمنظومات الاقتصادية والإعلامية الدولية، بحيث تمتد الاستفادة من النشاط الرياضي إلى خدمات ومزايا للأعضاء، وفرص للتطوير والتعاون الإعلامي والاقتصادي، وانفتاح على أسواق وشركاء جدد.',
        'ويتطلع نادي حلول العقاري العالمي إلى بناء شراكات استراتيجية مع الأندية الرياضية تقوم على التكامل والمصالح المشتركة، وتُترجم إلى خدمات ومبادرات عملية تتناسب مع أهداف كل نادٍ.',
      ],
    },
  ],
  closing: {
    eyebrow: 'نادي حلول العقاري العالمي',
    lines: ['تكامل.', 'مصالح مشتركة.', 'شراكة عملية.'],
    tagline: 'منظومة دولية للخدمات والعلاقات والفرص والإعلام الاقتصادي',
    name: 'د. أدهم الشرقاوي',
    role: 'المؤسس ورئيس مجلس الإدارة',
    websiteLabel: 'الموقع الإلكتروني',
  },
};
